import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import {
  adminGetUser,
  adminGetUserTrainingHistory,
  adminUpdateUser,
  adminDeleteUser,
  adminAssignManager,
  adminUnassignManager,
  adminListUsers,
  platformCertaSignKycSync,
  getPlatformRouteDeclarations,
  createPlatformSsmRevision,
  createPlatformSuRevision,
  generatePlatformRouteDeclarationPdf,
} from "../services/api";
import SsmRevisions from "../components/SsmRevisions";
import SuRevisions from "../components/SuRevisions";
import RouteDeclarationRevisions from "../components/RouteDeclarationRevisions";
import { loadSession } from "../auth";
import { useTranslation, Trans } from "react-i18next";

const TABS = ["details", "trainings", "docs"];

function RoleBadge({ roles = [] }) {
  const { t } = useTranslation();
  const r = Array.isArray(roles) ? roles : [];
  if (!r.length) return <span className="text-xs text-slate-400">{t("admin_users.no_roles")}</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {r.map((role) => (
        <span
          key={role}
          className="px-2 py-0.5 rounded-full text-xs border bg-slate-100 border-slate-200 text-slate-600"
        >
          {role}
        </span>
      ))}
    </div>
  );
}

// ─── Tab: Employee Details ────────────────────────────────────────────────────

function DetailsTab({ user, tenantUsers, onRefresh, onDeleted }) {
  const { t, i18n } = useTranslation();
  const session = loadSession();
  const me = session?.user;
  const isPlatformAdmin = me?.derived?.isPlatformAdmin || (Array.isArray(me?.roles) && me.roles.includes("platform_admin"));

  // Single panel state: null | "edit" | "assign" | "delete"
  const [activePanel, setActivePanel] = useState(null);

  const togglePanel = (panel) =>
    setActivePanel((prev) => (prev === panel ? null : panel));

  // Edit form
  const emptyForm = {
    name: user.name || "",
    email: user.email || "",
    password: "",
    isManager: !!user.derived?.isManager,
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleting, setDeleting] = useState(false);

  // Assign manager
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignForm, setAssignForm] = useState({
    managerId: user.derived?.managerId ? String(user.derived.managerId) : "",
    reportId: String(user.id),
    autoPromoteManager: true,
    replaceExisting: true,
  });

  // Sync assignForm when user data changes
  useEffect(() => {
    setAssignForm((f) => ({
      ...f,
      reportId: String(user.id),
      managerId: user.derived?.managerId ? String(user.derived.managerId) : "",
    }));
  }, [user]);

  // Reset edit form when opening the edit panel
  const openEdit = () => {
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      isManager: !!user.derived?.isManager,
      job_title: user.profile?.fields?.job_title || "",
      start_date: user.profile?.fields?.start_date || "",
      phone: user.profile?.fields?.phone || "",
      address: user.profile?.fields?.address || "",
      birth_place: user.profile?.fields?.birth_place || "",
    });
    togglePanel("edit");
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password || undefined,
        isManager: form.isManager,
      };
      if (isPlatformAdmin) {
        payload.job_title = String(form.job_title || "").trim();
        // Send empty string to explicitly clear the date if blank
        payload.start_date = typeof form.start_date === "string" ? form.start_date : form.start_date || "";
        // Optional manual profile fields
        if (typeof form.phone !== "undefined") payload.phone = String(form.phone || "").trim();
        if (typeof form.address !== "undefined") payload.address = String(form.address || "").trim();
        if (typeof form.birth_place !== "undefined") payload.birth_place = String(form.birth_place || "").trim();
      }
      await adminUpdateUser(user.id, payload);
      setActivePanel(null);
      onRefresh();
    } catch (err) {
      alert(err?.message || t("admin_users.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminDeleteUser(user.id);
      onDeleted();
    } catch (err) {
      alert(err?.message || t("admin_users.delete_failed"));
      setDeleting(false);
      setActivePanel(null);
    }
  };

  const handleAssign = async () => {
    if (!assignForm.managerId || !assignForm.reportId) {
      alert(t("admin_users.pick_both"));
      return;
    }
    if (assignForm.managerId === assignForm.reportId) {
      alert(t("admin_users.must_be_different"));
      return;
    }
    setAssignSubmitting(true);
    try {
      await adminAssignManager({
        managerId: assignForm.managerId,
        reportId: assignForm.reportId,
        autoPromoteManager: assignForm.autoPromoteManager,
        replaceExisting: assignForm.replaceExisting,
      });
      setActivePanel(null);
      onRefresh();
    } catch (err) {
      alert(err?.message || t("admin_users.assign_failed"));
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    if (!assignForm.managerId || !assignForm.reportId) {
      alert(t("admin_users.pick_both"));
      return;
    }
    setAssignSubmitting(true);
    try {
      await adminUnassignManager({
        managerId: assignForm.managerId,
        reportId: assignForm.reportId,
      });
      setActivePanel(null);
      onRefresh();
    } catch (err) {
      alert(err?.message || t("admin_users.unassign_failed"));
    } finally {
      setAssignSubmitting(false);
    }
  };

  // CertaSign KYC sync state and handler (platform_admin only)
  const [syncingKyc, setSyncingKyc] = useState(false);
  const [kycSyncMessage, setKycSyncMessage] = useState(null);

  const handleCertaSignSync = async () => {
    if (!isPlatformAdmin) return;
    setKycSyncMessage(null);
    setSyncingKyc(true);
    try {
      const res = await platformCertaSignKycSync({ userId: user.id, dryRun: false, overwrite: false });

      // Interpret backend response conservatively
      const status = (res && res.status) || null;
      const found = typeof res?.found !== "undefined" ? res.found : null;
      const kycStatus = res?.kyc_status || null;

      if (status === "not_found" || found === false) {
        setKycSyncMessage({ type: "info", text: t("emp_details.certasign_kyc.sync_not_found") });
      } else if (status === "not_approved" || kycStatus === "not_approved") {
        setKycSyncMessage({ type: "info", text: t("emp_details.certasign_kyc.sync_not_approved") });
      } else if (status === "updated" || status === "would_update" || (res && res.updated_fields)) {
        setKycSyncMessage({ type: "success", text: t("emp_details.certasign_kyc.sync_success") });
        // Refresh to show updated profile values
        try { await onRefresh(); } catch (e) { /* ignore refresh errors */ }
      } else if (status && String(status).startsWith("skipped")) {
        setKycSyncMessage({ type: "info", text: t("emp_details.certasign_kyc.sync_no_changes") });
      } else {
        // Default to success if backend reports ok but no specific status
        if (res && res.ok) {
          setKycSyncMessage({ type: "success", text: t("emp_details.certasign_kyc.sync_success") });
          try { await onRefresh(); } catch (e) { /* ignore */ }
        } else {
          setKycSyncMessage({ type: "info", text: t("emp_details.certasign_kyc.sync_no_changes") });
        }
      }
    } catch (err) {
      const safeMsg = err?.data?.message || err?.message || null;
      const display = (safeMsg && typeof safeMsg === "string" && safeMsg.length < 300) ? safeMsg : t("emp_details.certasign_kyc.sync_failed");
      setKycSyncMessage({ type: "error", text: display });
    } finally {
      setSyncingKyc(false);
    }
  };

  const isSelf = me?.id === user.id;

  return (
    <div className="space-y-4">
      {/* Info card */}
      <Card>
        <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("admin_users.label_name")}</dt>
            <dd className="font-medium text-slate-900">{user.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("admin_users.label_email")}</dt>
            <dd className="text-slate-700">{user.email}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("admin_users.table_roles")}</dt>
            <dd><RoleBadge roles={user.roles} /></dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("admin_users.table_is_manager")}</dt>
            <dd className="text-slate-700">{user.derived?.isManager ? t("admin_users.yes") : t("admin_users.no")}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("admin_users.table_manager_name")}</dt>
            <dd className="text-slate-700">{user.derived?.managerName || user.manager?.name || "—"}</dd>
          </div>
          {user.worksite && (
            <div>
              <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.worksite")}</dt>
              <dd className="text-slate-700">{user.worksite.name}</dd>
            </div>
          )}
          {user.tenantId && (
            <div>
              <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.tenant")}</dt>
              <dd className="font-mono text-xs text-slate-600">{user.tenantId}</dd>
            </div>
          )}
          {user.created_at && (
            <div>
              <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.created_at")}</dt>
              <dd className="text-slate-700">{new Date(user.created_at).toLocaleDateString()}</dd>
            </div>
          )}
        </dl>
      </Card>

      {/* CertaSign KYC read-only section (platform admins only) */}
      {user?.profile?.fields && (
        <Card>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm text-slate-700">{t("emp_details.certasign_kyc.title")}</h3>
            {isPlatformAdmin && (
              <div>
                <Button onClick={handleCertaSignSync} disabled={syncingKyc} variant={syncingKyc ? "primary" : "secondary"}>
                  {syncingKyc ? "…" : t("emp_details.certasign_kyc.sync_btn")}
                </Button>
              </div>
            )}
          </div>
          {kycSyncMessage && (
            <div className={`mb-3 text-sm ${kycSyncMessage.type === "success" ? "text-emerald-700" : kycSyncMessage.type === "error" ? "text-rose-700" : "text-slate-600"}`}>
              {kycSyncMessage.text}
            </div>
          )}
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {(() => {
              const f = user.profile.fields || {};
              const fmtDate = (iso) => {
                if (!iso) return "—";
                // Handle date-only strings YYYY-MM-DD or full ISO
                const ymd = String(iso).split("T")[0];
                const parts = ymd.split("-");
                if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
                try {
                  return new Date(iso).toLocaleDateString(i18n?.language || undefined);
                } catch (e) {
                  return iso;
                }
              };
              const fmtDateTime = (iso) => {
                if (!iso) return "—";
                try {
                  return new Date(iso).toLocaleString(i18n?.language || undefined);
                } catch (e) {
                  return iso;
                }
              };

              return (
                <>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.legal_last_name")}</dt>
                    <dd className="font-medium text-slate-900">{f.legal_last_name || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.legal_first_name")}</dt>
                    <dd className="text-slate-700">{f.legal_first_name || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.personal_identifier")}</dt>
                    <dd className="text-slate-700">{f.personal_identifier || f.identity_document_number || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.birth_date")}</dt>
                    <dd className="text-slate-700">{fmtDate(f.birth_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.sex")}</dt>
                    <dd className="text-slate-700">{f.sex || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.identity_document_number")}</dt>
                    <dd className="text-slate-700">{f.identity_document_number || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.identity_document_type")}</dt>
                    <dd className="text-slate-700">{f.identity_document_type || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.status")}</dt>
                    <dd className="text-slate-700">{f.certasign_kyc_status || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.verified_at")}</dt>
                    <dd className="text-slate-700">{fmtDateTime(f.certasign_kyc_verified_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.synced_at")}</dt>
                    <dd className="text-slate-700">{fmtDateTime(f.certasign_kyc_synced_at)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-slate-500 font-medium mb-0.5">{t("emp_details.certasign_kyc.last_error")}</dt>
                    <dd className="text-sm text-rose-700 bg-rose-50 rounded-md p-2">{f.certasign_kyc_last_error || "—"}</dd>
                  </div>
                </>
              );
            })()}
          </dl>
        </Card>
      )}

      {/* Actions — behave like toggles: clicking an active button closes its panel */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={openEdit} variant={activePanel === "edit" ? "primary" : "secondary"}>
          {t("emp_details.btn_edit")}
        </Button>
        <Button
          variant={activePanel === "assign" ? "primary" : "secondary"}
          onClick={() => togglePanel("assign")}
        >
          {t("emp_details.btn_assign_manager")}
        </Button>
        <Button
          variant="danger"
          onClick={() => !isSelf && togglePanel("delete")}
          disabled={isSelf}
          title={isSelf ? t("admin_users.cannot_delete_self") : ""}
          className={activePanel !== "delete" ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 shadow-none" : ""}
        >
          {t("emp_details.btn_delete_employee")}
        </Button>
      </div>

      {/* Edit form */}
      {activePanel === "edit" && (
        <Card title={t("admin_users.form_edit_title")}>
          <form className="grid sm:grid-cols-2 gap-3" onSubmit={handleSave}>
            <div>
              <label className="block text-xs text-slate-500 font-medium mb-1">{t("admin_users.label_name")}</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t("admin_users.ph_name")}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 font-medium mb-1">{t("admin_users.label_email")}</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder={t("admin_users.ph_email")}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 font-medium mb-1">{t("admin_users.label_password_new")}</label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={t("admin_users.ph_password_new")}
              />
            </div>
            {isPlatformAdmin && (
              <>
                <div>
                  <label className="block text-xs text-slate-500 font-medium mb-1">{t("onboarding.phone_label")}</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder={t("onboarding.phone_label")}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 font-medium mb-1">{t("onboarding.address_label")}</label>
                  <Input
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder={t("onboarding.address_label")}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 font-medium mb-1">{t("onboarding.birth_place_label")}</label>
                  <Input
                    value={form.birth_place}
                    onChange={(e) => setForm((f) => ({ ...f, birth_place: e.target.value }))}
                    placeholder={t("onboarding.birth_place_label")}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 font-medium mb-1">{t("onboarding.job_title_label")}</label>
                  <Input
                    value={form.job_title}
                    onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
                    placeholder={t("onboarding.job_title_label")}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 font-medium mb-1">{t("onboarding.start_date_label")}</label>
                  <Input
                    type="date"
                    value={form.start_date || ""}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
              </>
            )}
            <div className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                id="editIsManager"
                checked={form.isManager}
                onChange={(e) => setForm((f) => ({ ...f, isManager: e.target.checked }))}
              />
              <label htmlFor="editIsManager" className="text-sm text-slate-700">{t("admin_users.label_manager")}</label>
            </div>
            <div className="sm:col-span-2 flex gap-2 mt-2">
              <Button type="submit" disabled={saving}>
                {saving ? t("admin_users.btn_saving") : t("admin_users.btn_save")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setActivePanel(null)} disabled={saving}>
                {t("admin_users.btn_cancel")}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Assign manager panel */}
      {activePanel === "assign" && (
        <Card title={t("admin_users.assign_panel_title")}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-slate-500 font-medium mb-1">{t("admin_users.label_manager_select")}</label>
              <select
                className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white text-slate-700 w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                value={assignForm.managerId}
                onChange={(e) => setAssignForm((f) => ({ ...f, managerId: e.target.value }))}
              >
                <option value="">{t("admin_users.opt_select_manager")}</option>
                {tenantUsers
                  .filter((u) => u.id !== user.id)
                  .map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.name || u.email} {u.derived?.isManager ? "(manager)" : ""}
                    </option>
                  ))}
              </select>
            </div>
            <div className="sm:col-span-2 flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={assignForm.autoPromoteManager}
                  onChange={(e) => setAssignForm((f) => ({ ...f, autoPromoteManager: e.target.checked }))}
                />
                {t("admin_users.checkbox_auto_promote")}
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={assignForm.replaceExisting}
                  onChange={(e) => setAssignForm((f) => ({ ...f, replaceExisting: e.target.checked }))}
                />
                {t("admin_users.checkbox_replace_existing")}
              </label>
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <Button onClick={handleAssign} disabled={assignSubmitting}>
                {assignSubmitting ? t("admin_users.btn_assigning") : t("admin_users.btn_assign")}
              </Button>
              <Button variant="ghost" onClick={handleUnassign} disabled={assignSubmitting}>
                {t("admin_users.btn_unassign")}
              </Button>
              <Button variant="ghost" onClick={() => setActivePanel(null)} disabled={assignSubmitting}>
                {t("admin_users.btn_close")}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Delete confirm */}
      {activePanel === "delete" && (
        <div className="p-4 rounded-xl border border-red-200 bg-red-50">
          <div className="text-sm text-red-800">
            <Trans
              i18nKey="admin_users.delete_confirm_title"
              values={{ email: user.email }}
              components={{ 1: <span className="font-medium" /> }}
            />
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={handleDelete} variant="danger" disabled={deleting}>
              {deleting ? "…" : t("admin_users.btn_delete_confirm")}
            </Button>
            <Button variant="ghost" onClick={() => setActivePanel(null)} disabled={deleting}>
              {t("admin_users.btn_cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Training History ─────────────────────────────────────────────────────

const STATUS_CLASSES = {
  completed:   "bg-emerald-50 text-emerald-700 border border-emerald-200",
  in_progress: "bg-amber-50 text-amber-700 border border-amber-200",
  not_started: "bg-slate-100 text-slate-600 border border-slate-200",
};

function TrainingSection({ title, assignments, statusFilter, onFilterChange, t, i18n, colorClass }) {
  const filtered = assignments.filter((a) => {
    if (statusFilter === "all") return true;
    const group = a.status_group || "not_started";
    return group === statusFilter;
  });

  const dateDisplay = (a) => {
    const group = a.status_group || "not_started";
    if (group === "completed" && a.completion_date_display) {
      return new Date(a.completion_date_display).toLocaleDateString(i18n?.language);
    }
    if (a.due_date) return new Date(a.due_date).toLocaleDateString(i18n?.language);
    return "—";
  };

  const dateLabel = statusFilter === "completed"
    ? t("training_history.completion_date")
    : statusFilter === "all"
    ? t("training_history.col_date")
    : t("training_history.due_date");

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-semibold text-sm ${colorClass}`}>{title}</h3>
        <select
          className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
          value={statusFilter}
          onChange={(e) => onFilterChange(e.target.value)}
        >
          <option value="all">{t("training_history.filter_all")}</option>
          <option value="completed">{t("training_history.filter_completed")}</option>
          <option value="in_progress">{t("training_history.filter_in_progress")}</option>
          <option value="not_started">{t("training_history.filter_not_started")}</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <div className="text-sm text-slate-400 py-3 text-center">
          {title.includes("SSM") ? t("training_history.no_ssm") : t("training_history.no_su")}
        </div>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left border-b border-slate-200">
              <th className="py-2 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("training_history.col_training_name")}</th>
              {statusFilter === "all" && (
                <th className="py-2 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("training_history.col_status")}</th>
              )}
              <th className="py-2 pr-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">{dateLabel}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((a) => {
              const group = a.status_group || "not_started";
              return (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="py-2.5 pr-4 font-medium text-slate-900">
                    {a.training_title || a.training_slug || `#${a.training_id}`}
                  </td>
                  {statusFilter === "all" && (
                    <td className="py-2.5 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CLASSES[group] || STATUS_CLASSES.not_started}`}>
                        {t(`training_history.filter_${group}`)}
                      </span>
                    </td>
                  )}
                  <td className="py-2.5 pr-4 text-slate-500">{dateDisplay(a)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TrainingsTab({ userId }) {
  const { t, i18n } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ssmFilter, setSsmFilter] = useState("all");
  const [suFilter, setSuFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr("");
    adminGetUserTrainingHistory(userId)
      .then((data) => {
        if (!cancelled) setAssignments(data.assignments || []);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.message || t("emp_details.error_trainings"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, t]);

  if (loading) return <div className="text-sm text-slate-500 py-4">{t("admin_users.loading")}</div>;
  if (err) return <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</div>;

  const ssmAssignments = assignments.filter((a) => a.ssm_required);
  const suAssignments = assignments.filter((a) => a.su_required);

  return (
    <div className="space-y-6">
      <Card>
        <TrainingSection
          title={t("training_history.ssm_title")}
          assignments={ssmAssignments}
          statusFilter={ssmFilter}
          onFilterChange={setSsmFilter}
          t={t}
          i18n={i18n}
          colorClass="text-blue-700"
        />
      </Card>
      <Card>
        <TrainingSection
          title={t("training_history.su_title")}
          assignments={suAssignments}
          statusFilter={suFilter}
          onFilterChange={setSuFilter}
          t={t}
          i18n={i18n}
          colorClass="text-purple-700"
        />
      </Card>
    </div>
  );
}

// Download button helper removed (not used in current UI)

// Tests table removed — not used in current UI

// ─── Tab: Documentation ───────────────────────────────────────────────────────

// DocSection removed — not used in current UI

function DocsTab({ userId, user }) {
  const { t } = useTranslation();
  const session = loadSession();
  const me = session?.user;
  const isPlatformAdmin = me?.derived?.isPlatformAdmin || (Array.isArray(me?.roles) && me.roles.includes("platform_admin"));
  const [activeDocTab, setActiveDocTab] = useState("ssm");
  const [ssmEligibleDecl, setSsmEligibleDecl] = useState(null);
  const [ssmChecking, setSsmChecking] = useState(false);
  const [ssmGenerating, setSsmGenerating] = useState(false);
  const [ssmMessage, setSsmMessage] = useState(null);
  const [suGenerating, setSuGenerating] = useState(false);
  const [suMessage, setSuMessage] = useState(null);
  const [rdGenerating, setRdGenerating] = useState(false);
  const [rdMessage, setRdMessage] = useState(null);

  // Check for an approved/signed route declaration for this user (newest first)
  useEffect(() => {
    let cancelled = false;
    async function checkEligible() {
      setSsmChecking(true);
      setSsmMessage(null);
      try {
        // Try approved first
        const dataA = await getPlatformRouteDeclarations({ status: "approved", userId });
        const listA = Array.isArray(dataA) ? dataA : dataA.items ?? [];
        let items = listA || [];
        if (!items.length) {
          const dataS = await getPlatformRouteDeclarations({ status: "signed", userId });
          items = Array.isArray(dataS) ? dataS : dataS.items ?? [];
        }
        if (!cancelled) setSsmEligibleDecl(items && items.length ? items[0] : null);
      } catch (e) {
        if (!cancelled) setSsmEligibleDecl(null);
      } finally {
        if (!cancelled) setSsmChecking(false);
      }
    }

    if (isPlatformAdmin && userId) checkEligible();
    else setSsmEligibleDecl(null);

    return () => { cancelled = true; };
  }, [userId, isPlatformAdmin]);

  async function handleGenerateSsm() {
    if (!ssmEligibleDecl) return;
    setSsmGenerating(true);
    setSsmMessage(null);
    try {
      await createPlatformSsmRevision(ssmEligibleDecl.id);
      // Notify SsmRevisions to refresh
      try { window.dispatchEvent(new CustomEvent("ssmRevisionCreated", { detail: { userId } })); } catch (e) {}
      setSsmMessage("Revizia SSM a fost generată.");
      // also re-check eligibility in case of conflicts
      try {
        const dataA = await getPlatformRouteDeclarations({ status: "approved", userId });
        const listA = Array.isArray(dataA) ? dataA : dataA.items ?? [];
        let items = listA || [];
        if (!items.length) {
          const dataS = await getPlatformRouteDeclarations({ status: "signed", userId });
          items = Array.isArray(dataS) ? dataS : dataS.items ?? [];
        }
        setSsmEligibleDecl(items && items.length ? items[0] : null);
      } catch (e) {
        setSsmEligibleDecl(null);
      }
    } catch (e) {
      const code = e?.status;
      const msg = (e?.message || "").toString();
      if (code === 400 && msg.includes("pending_approval")) {
        alert("Declarația este în așteptare pentru aprobare.");
      } else if (code === 409 || msg.includes("conflict")) {
        alert("Există deja o revizie generată care trebuie finalizată înainte de a genera următoarea revizie.");
        try { window.dispatchEvent(new CustomEvent("ssmRevisionCreated", { detail: { userId } })); } catch (e) {}
      } else if (code === 400 && msg.includes("max_slots_exceeded")) {
        alert("Șablonul actual permite maximum 3 declarații de traseu.");
      } else if (code === 404) {
        alert("Declarația nu a fost găsită.");
      } else if (msg.includes("responsabil") || msg.includes("WorkManagement")) {
        alert("Nu este configurat responsabilul WorkManagement pentru acest angajator.");
      } else if (msg.includes("start_date") || msg.includes("angajării") || msg.includes("data_angajarii")) {
        alert("Data angajării lipsește pentru acest angajat.");
      } else if (msg.includes("functie") || msg.includes("job_title")) {
        alert("Funcția lipsește pentru acest angajat.");
      } else {
        alert(e?.message || "Eroare la generare fișă SSM");
      }
    } finally {
      setSsmGenerating(false);
    }
  }

  async function handleGenerateSu() {
    // SU generation is user-based and only shown to platform admins when
    // viewing a non-platform user. The backend performs final validation.
    setSuGenerating(true);
    setSuMessage(null);
    try {
      await createPlatformSuRevision(userId);
      try { window.dispatchEvent(new CustomEvent("suRevisionCreated", { detail: { userId } })); } catch (e) {}
      setSuMessage("Fișa SU a fost generată.");
    } catch (e) {
      const code = e?.status;
      const msg = (e?.message || "").toString();
      if (code === 409 || msg.toLowerCase().includes("conflict") || msg.toLowerCase().includes("already exists")) {
        alert("Există deja o fișă SU generată pentru acest utilizator.");
        try { window.dispatchEvent(new CustomEvent("suRevisionCreated", { detail: { userId } })); } catch (er) {}
      } else if (code === 400) {
        // backend returns Romanian validation messages; surface them when present
        alert(msg || "Date invalide pentru generare SU.");
      } else if (code === 404) {
        alert("Utilizatorul nu a fost găsit.");
      } else {
        alert(e?.message || "Eroare la generare fișă SU");
      }
    } finally {
      setSuGenerating(false);
    }
  }

  async function handleGenerateRouteDeclarationPdf() {
    if (!ssmEligibleDecl) return;
    setRdGenerating(true);
    setRdMessage(null);
    try {
      await generatePlatformRouteDeclarationPdf(ssmEligibleDecl.id);
      try { window.dispatchEvent(new CustomEvent("routeDeclarationRevisionCreated", { detail: { userId } })); } catch (e) {}
      setRdMessage("Declarația de traseu a fost generată.");
    } catch (e) {
      const code = e?.status;
      const msg = (e?.message || "").toString();
      if (code === 409 || msg.toLowerCase().includes("conflict") || msg.toLowerCase().includes("already exists")) {
        alert("Există deja o declarație de traseu generată pentru această declarație.");
        try { window.dispatchEvent(new CustomEvent("routeDeclarationRevisionCreated", { detail: { userId } })); } catch (e) {}
      } else if (code === 400 && (msg.includes("not approved") || msg.includes("pending_approval"))) {
        alert("Declarația de traseu nu este aprobată.");
      } else if (code === 404) {
        alert("Declarația nu a fost găsită.");
      } else {
        alert(msg || "Eroare la generare declarație de traseu");
      }
    } finally {
      setRdGenerating(false);
    }
  }

  // Training/tests table removed from Documentation tab; revisions lists are shown instead

  const DOC_TABS = [
    { key: "ssm", label: t("docs.ssm_title"), color: "blue" },
    { key: "route", label: "Declarație de Traseu", color: "teal" },
    { key: "su", label: t("docs.su_title"), color: "purple" },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-tab bar */}
      <div className="flex gap-1 border-b border-slate-200">
        {DOC_TABS.map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setActiveDocTab(key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeDocTab === key
                ? `border-${color}-600 text-${color}-700`
                : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* SSM */}
      {activeDocTab === "ssm" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-blue-700">{t("docs.ssm_title")}</h3>
            {isPlatformAdmin && (
              <button
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleGenerateSsm}
                disabled={!ssmEligibleDecl || ssmGenerating || ssmChecking}
              >
                {ssmGenerating ? "Se generează..." : "Generează fișa SSM"}
              </button>
            )}
          </div>
          {ssmMessage && <div className="text-sm text-emerald-700 mb-3">{ssmMessage}</div>}
          {isPlatformAdmin ? (
            <SsmRevisions userId={userId} />
          ) : (
            <div className="text-sm text-slate-500 py-2">Reviziile SSM sunt disponibile doar pentru administratorii platformei.</div>
          )}
        </Card>
      )}

      {/* Route Declaration */}
      {activeDocTab === "route" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-teal-700">Declarație de Traseu</h3>
            {isPlatformAdmin && (
              <button
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-teal-700 text-white hover:bg-teal-800 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleGenerateRouteDeclarationPdf}
                disabled={!ssmEligibleDecl || rdGenerating || ssmChecking}
                title={!ssmEligibleDecl ? "Nicio declarație de traseu aprobată" : undefined}
              >
                {rdGenerating ? "Se generează..." : "Generează PDF"}
              </button>
            )}
          </div>
          {rdMessage && <div className="text-sm text-emerald-700 mb-3">{rdMessage}</div>}
          {isPlatformAdmin ? (
            <RouteDeclarationRevisions userId={userId} />
          ) : (
            <div className="text-sm text-slate-500 py-2">Disponibil doar pentru administratorii platformei.</div>
          )}
        </Card>
      )}

      {/* SU */}
      {activeDocTab === "su" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-purple-700">{t("docs.su_title")}</h3>
            {isPlatformAdmin && !(user && Array.isArray(user.roles) && user.roles.includes("platform_admin")) && (
              <button
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-700 text-white hover:bg-purple-800 shadow-sm transition-colors disabled:opacity-50"
                onClick={handleGenerateSu}
                disabled={suGenerating}
              >
                {suGenerating ? "Se generează..." : "Generează fișa SU"}
              </button>
            )}
          </div>
          {suMessage && <div className="text-sm text-emerald-700 mb-3">{suMessage}</div>}
          {isPlatformAdmin ? (
            <SuRevisions userId={userId} />
          ) : (
            <div className="text-sm text-slate-500 py-2">Reviziile SU sunt disponibile doar pentru administratorii platformei.</div>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminEmployeeDetails({ overrideUserId, onClose }) {
  const params = useParams();
  const navigate = useNavigate();
  const id = overrideUserId ?? params?.id;
  const isPlatformRoute = !onClose && window.location.pathname.startsWith("/platform/users");
  const { t } = useTranslation();
  const session = loadSession();
  const me = session?.user;

  const isOrgAdmin =
    me?.derived?.isOrgAdmin ||
    (Array.isArray(me?.roles) && me.roles.includes("org_super_admin"));
    const isPlatformAdmin = Array.isArray(me?.roles) && me.roles.includes("platform_admin");

  // Determine if this admin is worksite-scoped
  const myWorksiteId = me?.worksite_id ?? null;
  const isWorksiteScoped = isOrgAdmin && myWorksiteId !== null;

  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userErr, setUserErr] = useState("");
  const [activeTab, setActiveTab] = useState("details");

  // Tenant/worksite users list (needed for assign-manager dropdown)
  const [tenantUsers, setTenantUsers] = useState([]);

  async function loadUser() {
    setUserLoading(true);
    setUserErr("");
    try {
      const data = await adminGetUser(id);
      setUser(data.user);
    } catch (e) {
      if (e.status === 404) {
        setUserErr("not_found");
      } else if (e.status === 403) {
        setUserErr("not_found"); // treat forbidden as not found for security
      } else {
        setUserErr(e?.message || t("emp_details.error_user"));
      }
    } finally {
      setUserLoading(false);
    }
  }

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load users for assign-manager dropdown: scoped to worksite when applicable
  useEffect(() => {
    if (!isOrgAdmin && !isPlatformAdmin) return;
    const params = { pageSize: 100 };
    if (isWorksiteScoped) params.worksiteId = String(myWorksiteId);
    adminListUsers(params)
      .then((data) => setTenantUsers(data.items || []))
      .catch(() => {});
  }, [isOrgAdmin, isPlatformAdmin, isWorksiteScoped, myWorksiteId]);

  if (!isOrgAdmin && !isPlatformAdmin) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-slate-600">{t("admin_users.need_role")}</div>
        </Card>
      </div>
    );
  }

  if (userLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="text-sm text-slate-500">{t("admin_users.loading")}</div>
      </div>
    );
  }

  if (userErr) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <button
          onClick={() => (onClose ? onClose() : navigate(isPlatformRoute ? "/platform/dashboard" : "/admin/users"))}
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors"
        >
          ← {t("emp_details.back_to_employees")}
        </button>
        <Card>
          <div className="text-sm text-red-600">
            {userErr === "not_found" ? t("emp_details.not_found") : userErr}
          </div>
        </Card>
      </div>
    );
  }

  const tabLabels = {
    details: t("emp_details.tab_details"),
    trainings: t("emp_details.tab_trainings"),
    docs: t("emp_details.tab_docs"),
  };

  // Initials avatar
  const initials = (user.name || user.email || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      {/* Back link */}
      <button
        onClick={() => (onClose ? onClose() : navigate(isPlatformRoute ? "/platform/dashboard" : "/admin/users"))}
        className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors"
      >
        ← {t("emp_details.back_to_employees")}
      </button>

      {/* Profile header card */}
      <Card>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 text-lg font-bold flex items-center justify-center flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">{user.name || user.email}</h1>
            {user.name && <p className="text-sm text-slate-500 truncate">{user.email}</p>}
            <div className="mt-1.5">
              <RoleBadge roles={user.roles} />
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
            }`}
          >
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === "details" && (
          <DetailsTab
            user={user}
            tenantUsers={tenantUsers}
            onRefresh={loadUser}
            onDeleted={() => (onClose ? onClose() : navigate(isPlatformRoute ? "/platform/dashboard" : "/admin/users"))}
          />
        )}
        {activeTab === "trainings" && <TrainingsTab userId={id} />}
        {activeTab === "docs" && <DocsTab userId={id} user={user} />}
      </div>
    </div>
  );
}
